import { apiClient } from '@/shared/api/client';
import type { ApiResponse } from '@/shared/types/api.types';
import { malaysiaScraping, type SsmLoginResponse } from './malaysiaScraping';

// Re-export SsmLoginResponse for convenience
export type { SsmLoginResponse };

/**
 * Reserve Name API
 * 말레이시아 회사명 예약 업무 전용 API
 *
 * 엔드포인트: /api/marketplace/v1/malaysia/entity/reserve-name
 */

/**
 * Reserve Name 요청 타입
 */
export interface ReserveNameRequest {
  credentialId?: string;
  sessionId: string;
  entityName: string;
  purpose: string; // EntityPurpose value
  companyType: string; // EntityCompanyType value
  subType: string; // EntitySubType value
  uniqueEntityNumber?: string;
  clarification01?: string;
  clarification02?: string;
  clarification03?: ClarificationWithFile;
  clarification04?: ClarificationWithFile;
  clarification05?: ClarificationWithFile;
  clarification07?: string; // 7) Other Comments
  /** 6) Business Code — MSIC 코드 문자열. CRS 는 최대 3건. */
  businessCodes?: string[];
  promoters: PromoterInfo[];
}

/**
 * Clarification with File 타입
 */
export interface ClarificationWithFile {
  text: string;
  fileName?: string;
  fileContentBase64?: string;
  contentType?: string;
}

/**
 * Promoter 정보 타입.
 *
 * 2026-09-28 CRS Add Promoter 모달 실측 기준으로 분기 필드를 모두 포함한다.
 *   개인  — 신분증 종류에 따라 만료일(MYKAS) 또는 2차 신분증 3칸(여권)이 붙는다.
 *   법인  — SSM 등록 여부로 갈린다: 등록(등록유형/표기/등록번호) vs 미등록(멤버분류/법인번호).
 * `promoterName` 은 개인이면 Individual Name, 법인이면 Entity Name 이다.
 */
export interface PromoterInfo {
  promoterType: string; // INDIVIDUAL, BODY_CORPORATE
  promoterName: string;
  identificationType: string; // NRIC(MYKAD), MYKAS, PR_NUMBER, ARMY_ID, PASSPORT
  identificationNumber: string;
  dateOfBirth?: string; // YYYY-MM-DD
  email?: string;
  salutation?: string;
  /** MYKAS 일 때만 필수(1차 신분증 만료일). */
  idExpiryDate?: string;
  /** 여권(FOREIGNER NATIONAL ID) 일 때만 필수. */
  secondaryIdType?: string;
  secondaryIdNumber?: string;
  secondaryIdExpiryDate?: string;
  /** 법인 — 'true' | 'false' (셀렉트 값이라 문자열). */
  isSsmRegistered?: string;
  compRegType?: string;
  compRegNoFormat?: string;
  compRegNo?: string;
  memberCategory?: string;
  memberSubCategory?: string;
  entityNo?: string;
}

/**
 * Reserve Name 응답 타입
 */
export interface ReserveNameResponse {
  success: boolean;
  error?: string;
  html?: string;
  pdfLength?: number;
  pdfDataBase64?: string;
  reservationNumber?: string;
  expiryDate?: string;
}

const MONTH_NAMES = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];

/** Convert "DD MMMM YYYY" (e.g., "11 JULY 1983") to "YYYY-MM-DD" */
function toISODate(dateStr?: string): string {
  if (!dateStr) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const match = dateStr.match(/^(\d{1,2})\s+([A-Z]+)\s+(\d{4})$/);
  if (match) {
    const monthIdx = MONTH_NAMES.indexOf(match[2]);
    if (monthIdx >= 0) {
      return `${match[3]}-${String(monthIdx + 1).padStart(2, '0')}-${match[1].padStart(2, '0')}`;
    }
  }
  return dateStr;
}

/**
 * Reserve Name API
 */
export const reserveNameApi = {
  /**
   * 이름 예약 폼 조립 재료 — CRS 코드표 + 선언문 + 제출인 정보.
   * `tables` 로 필요한 코드표만 고른다(생략하면 약 985KB).
   */
  getCrsScaffold: async (params: { crsCredentialId?: string | null; tables?: readonly string[] }) => {
    return apiClient.post<{
      lookup: Record<string, any[]>;
      /** 이름 예약 전용 코드표 — Promoter 코드는 설립 표가 아니라 이쪽이 정본이다. */
      ns_lookup: Record<string, any>;
      declaration: Record<string, any>;
      lodger: Record<string, any>;
      missing_tables: string[];
    }>(
      '/marketplace/v1/malaysia/entity/name-reservation-scaffold',
      {
        crs_credential_id: params.crsCredentialId || undefined,
        tables: params.tables ? [...params.tables] : undefined,
      },
      { timeout: 120000 },
    );
  },

  /**
   * CRS 이름 예약 draft 생성/갱신 (제출/결제 아님).
   * 조립한 payload 를 그대로 CRS rocNameReservation/saveDraft 로 보낸다.
   * 성공 시 submission_id 를 돌려준다 — CRS 에서 이어서 작성하거나 삭제할 수 있다.
   */
  createCrsDraft: async (params: { payload: Record<string, any>; crsCredentialId?: string | null }) => {
    return apiClient.post<{
      submission_id: string;
      name_reservation_id: string;
      roc_name_reservation_id: string;
      lodging_ref_no?: string;
      status_msg?: string;
    }>(
      '/marketplace/v1/malaysia/entity/name-reservation-draft',
      { payload: params.payload, crs_credential_id: params.crsCredentialId || undefined },
      { timeout: 120000 },
    );
  },

  /**
   * 기존 draft 상세 — 갱신 저장 시 하위 항목(clarification/promoter) id 를 유지하려고 먼저 읽는다.
   * 빈 id 로 저장하면 CRS 가 행을 중복 생성할 수 있다(설립에서 실측된 동작).
   */
  getCrsDraftDetail: async (params: { crsCredentialId?: string | null; submissionId: string }) => {
    return apiClient.post<{ detail: any; status_msg?: string }>(
      '/marketplace/v1/malaysia/entity/name-reservation-detail',
      { crs_credential_id: params.crsCredentialId || undefined, submission_id: params.submissionId },
      { timeout: 120000 },
    );
  },

  /**
   * SSM 로그인 (공통 API 재사용)
   * - DB에 유효 세션(10분 이내) 있으면 재사용
   */
  ssmLogin: (credentialId: string): Promise<SsmLoginResponse> => {
    return malaysiaScraping.ssmLogin(credentialId, 'mycoid2016');
  },

  /**
   * SSM 로그아웃
   */
  ssmLogout: (sessionId: string) => {
    return malaysiaScraping.ssmLogout(sessionId);
  },

  /**
   * @deprecated MyCoID2016 시절 경로다. **동작하지 않는다** —
   *   백엔드가 넘기는 파이썬 데몬의 `/entity/reserve-name` 이 존재하지 않는다
   *   (MY 는 CRS 전용으로 옮겨졌다). 이름 예약은 `createCrsDraft` 를 쓴다.
   *   화면에서는 더 이상 호출하지 않으며, 지우지 않은 이유는 reserveNameStore 가
   *   아직 이 시그니처를 참조하고 있어서다(그 스토어도 사용처가 없다).
   */
  reserveName: async (data: ReserveNameRequest): Promise<ApiResponse<ReserveNameResponse>> => {
    console.log('📤 [ReserveNameApi] Reserve Name request:', {
      entityName: data.entityName,
      purpose: data.purpose,
      companyType: data.companyType,
      promoterCount: data.promoters?.length
    });

    // camelCase → snake_case 변환
    const requestData = {
      credential_id: data.credentialId,
      session_id: data.sessionId,
      entity_name: data.entityName,
      purpose: data.purpose,
      company_type: data.companyType,
      sub_type: data.subType,
      unique_entity_number: data.uniqueEntityNumber,
      clarification01: data.clarification01,
      clarification02: data.clarification02,
      clarification03: data.clarification03 ? {
        text: data.clarification03.text,
        file_name: data.clarification03.fileName,
        file_content_base64: data.clarification03.fileContentBase64,
        content_type: data.clarification03.contentType,
      } : undefined,
      clarification04: data.clarification04 ? {
        text: data.clarification04.text,
        file_name: data.clarification04.fileName,
        file_content_base64: data.clarification04.fileContentBase64,
        content_type: data.clarification04.contentType,
      } : undefined,
      clarification05: data.clarification05 ? {
        text: data.clarification05.text,
        file_name: data.clarification05.fileName,
        file_content_base64: data.clarification05.fileContentBase64,
        content_type: data.clarification05.contentType,
      } : undefined,
      clarification07: data.clarification07,
      business_codes: data.businessCodes,
      promoters: data.promoters.map(p => ({
        promoter_type: p.promoterType,
        promoter_name: p.promoterName,
        identification_type: p.identificationType,
        identification_number: p.identificationNumber,
        date_of_birth: toISODate(p.dateOfBirth),
        email: p.email,
        salutation: p.salutation,
        id_expiry_date: toISODate(p.idExpiryDate),
        secondary_identification_type: p.secondaryIdType,
        secondary_identification_number: p.secondaryIdNumber,
        secondary_identification_expiry_date: toISODate(p.secondaryIdExpiryDate),
        is_ssm_registered: p.isSsmRegistered,
        company_registration_type: p.compRegType,
        company_registration_no_format: p.compRegNoFormat,
        company_registration_no: p.compRegNo,
        member_category: p.memberCategory,
        member_sub_category: p.memberSubCategory,
        entity_no: p.entityNo,
      })),
    };

    const response = await apiClient.post<ReserveNameResponse>('/marketplace/v1/malaysia/entity/reserve-name', requestData);

    console.log('📥 [ReserveNameApi] Reserve Name response:', {
      success: (response as any)?.success || (response as any)?.data?.success
    });

    return response;
  },
};

export default reserveNameApi;
